import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { flushAnalytics } from '@bonusly/analytics';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { useLocalStorage } from '../../lib/storage/hooks/use-local-storage';
import { getActiveSeasonConfig } from './seasonal-config';

interface SeasonalThemeContextValue {
	/** True if the user has the feature flag enabled (can see and use seasonal themes) */
	hasSeasonalThemePermission: boolean;
	/** True if user has permission AND has toggled the theme on */
	isSeasonalThemeEnabled: boolean;
	/**
	 * The local date (YYYY-MM-DD) the user first enabled the active season's
	 * theme. `null` when the theme has never been enabled. Once set, it never
	 * changes for that user/season.
	 */
	enableDate: string | null;
	toggleTheme: () => void;
}

export const SeasonalThemeContext = createContext<SeasonalThemeContextValue | undefined>(undefined);

const enabledAtKey = (storageKey: string) => `${storageKey}:enabled-at`;

const todayLocalDate = () => {
	const now = new Date();
	const yyyy = now.getFullYear();
	const mm = String(now.getMonth() + 1).padStart(2, '0');
	const dd = String(now.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

const readEnableDate = (storageKey: string): string | null => {
	try {
		const raw = localStorage.getItem(enabledAtKey(storageKey));
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return typeof parsed === 'string' ? parsed : null;
	} catch {
		return null;
	}
};

const writeEnableDate = (storageKey: string, date: string) => {
	try {
		localStorage.setItem(enabledAtKey(storageKey), JSON.stringify(date));
	} catch {
		// localStorage unavailable — best effort
	}
};

export const SeasonalThemeProvider: FC<PropsWithChildren> = ({ children }) => {
	const hasSeasonalThemesFlag = useFeatureFlag('seasonal-themes');
	const activeSeasonConfig = getActiveSeasonConfig();
	const [userPreference, setUserPreference] = useLocalStorage<boolean>(activeSeasonConfig.storageKey, false);
	const [enableDate, setEnableDate] = useLocalStorage<string | null>(enabledAtKey(activeSeasonConfig.storageKey), null);

	const flagWasEverTrueRef = useRef(false);
	if (hasSeasonalThemesFlag) {
		flagWasEverTrueRef.current = true;
	}

	const isSeasonalThemeEnabled = Boolean(hasSeasonalThemesFlag && userPreference);

	// Synchronously derive the effective enableDate so consumers see a non-null
	// value on the very first render. Without this, a user who already had the
	// theme on before per-user enable-date tracking shipped would see
	// `enableDate === null` on initial render, the bloom orchestration child
	// effect would bail with `[]` deps, and the floor wouldn't apply until the
	// next reload — affecting the entire pre-release cohort of opted-in users.
	const fallbackEnableDate = activeSeasonConfig.rolloutDate ?? todayLocalDate();
	const effectiveEnableDate = enableDate ?? (userPreference ? fallbackEnableDate : null);

	// Persist the backfill so the synchronous fallback above only fires once
	// per user. Once written, subsequent renders read the stored value directly.
	useEffect(() => {
		if (userPreference && !enableDate) {
			setEnableDate(fallbackEnableDate);
		}
	}, [userPreference, enableDate, setEnableDate, fallbackEnableDate]);

	// Clear stale preference when the feature flag is deliberately turned off.
	// The ref guard prevents clearing during initial load when the flag defaults
	// to false before the identity store has finished initializing.
	useEffect(() => {
		if (flagWasEverTrueRef.current && !hasSeasonalThemesFlag && userPreference) {
			setUserPreference(false);
		}
	}, [hasSeasonalThemesFlag, userPreference, setUserPreference]);

	const toggleTheme = useCallback(async () => {
		const newValue = !userPreference;
		// Write directly to localStorage before reload — useLocalStorage
		// persists via useEffect which wouldn't fire before the reload.
		localStorage.setItem(activeSeasonConfig.storageKey, JSON.stringify(newValue));

		// On the false → true transition, stamp enableDate if it's not already
		// set. Once set, never overwrite — so toggling off and on again preserves
		// the original opt-in date.
		if (newValue && !readEnableDate(activeSeasonConfig.storageKey)) {
			writeEnableDate(activeSeasonConfig.storageKey, todayLocalDate());
		}

		// Flush queued Amplitude events (e.g. SeasonalTheme.Enabled/Disabled fired
		// by the caller) before tearing down the page — the reload would otherwise
		// cancel any in-flight tracking requests. Wrapped in try/finally so a
		// rejected flush (network error, adblocker, etc.) never prevents the
		// reload: analytics are best-effort, but the theme toggle must complete.
		try {
			await flushAnalytics();
		} catch {
			// Intentionally swallowed — losing a tracking event is an acceptable
			// tradeoff for guaranteeing the UI reaches a consistent state.
		}
		window.location.reload();
	}, [userPreference, activeSeasonConfig.storageKey]);

	const contextValue: SeasonalThemeContextValue = useMemo(
		() => ({
			hasSeasonalThemePermission: Boolean(hasSeasonalThemesFlag),
			isSeasonalThemeEnabled,
			enableDate: effectiveEnableDate,
			toggleTheme,
		}),
		[hasSeasonalThemesFlag, isSeasonalThemeEnabled, effectiveEnableDate, toggleTheme]
	);

	return <SeasonalThemeContext.Provider value={contextValue}>{children}</SeasonalThemeContext.Provider>;
};

/**
 * Hook to access seasonal theme state and controls
 *
 * Returns safe defaults when used outside of SeasonalThemeProvider:
 * - hasSeasonalThemePermission: false
 * - isSeasonalThemeEnabled: false
 * - toggleTheme: no-op function
 *
 * This allows components to gracefully degrade in contexts without the provider.
 */
export const useSeasonalTheme = () => {
	const context = useContext(SeasonalThemeContext);

	if (!context) {
		return {
			hasSeasonalThemePermission: false,
			isSeasonalThemeEnabled: false,
			enableDate: null,
			toggleTheme: () => {},
		};
	}

	return context;
};
